import React, { useEffect, useState } from 'react';

import Header from './components/Header/Header';
import SelectNumber from './components/NumberSelection/NumberSelect';
import ThreadList from './components/ThreadList/ThreadList';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import MessageContainer from './components/MessageContainer/MessageContainer';

import { useUserDetailsContext } from 'context/userDetailsContext';
import useTextMessage from './hooks/useTextMessage';
import { setAppTitle } from 'utils/utils';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { markTextMessagesAsRead } from 'services/textMessageService';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useNavbarContext } from 'context/NavbarContext';
import useDynamicHeight from 'components/UI/NavbarHeight/useDynamicHeight';

export default function TextMessages({ isFromCase, caseDetails, isFromLead, leadDetails, onMountUnMount, onOperationBreak }) {
  const userContext = useUserDetailsContext();
  const { contactId, phoneNumber } = useParams();
  const { addToast } = useToast();
  const navbarContext = useNavbarContext();
  const loadNotificationCount = navbarContext?.notificationData?.loadNotificationCount;
  const [selectedThreadsForMarkRead, setSelectedThreadsForMarkRead] = useState([]);
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);

  const { navHeight } = useDynamicHeight();

  const {
    messageThreads,
    setMessageThreads,
    selectedThread,
    handleSelectThread,
    selectedContact,
    setSelectedContact,
    newThreadActive,
    setNewThreadActive,
    activeIndex,
    setActiveIndex,
    fetchTextMessages,
    navigateToLatest,
    loadThreads,
    isLoading,
    selectedPracticeAreas,
    setSelectedPracticeAreas,
    setPageCount,
    setIsLastPage,
  } = useTextMessage(leadDetails, caseDetails, isFromCase, isFromLead, contactId, phoneNumber);

  useEffect(() => {
    // set the app tab title
    setAppTitle('Text Messages');

    // clean up function
    return () => {
      setAppTitle();
    };
  }, []);

  // Clean up selected threads when they become read
  // Using useRef to prevent infinite loops
  const lastMessageThreadsRef = React.useRef();

  useEffect(() => {
    // Skip if threads haven't actually changed
    if (lastMessageThreadsRef.current === messageThreads) {
      return;
    }
    lastMessageThreadsRef.current = messageThreads;

    if (messageThreads?.length > 0 && selectedThreadsForMarkRead.length > 0) {
      const tenantId = userContext?.userDetails?.tenant_id;
      const firmPhoneNumber = userContext?.firmPhoneNumber;

      // Filter out threads that are now read
      const validSelectedThreads = selectedThreadsForMarkRead.filter((messagePk) => {
        // Find the thread in messageThreads
        const thread = messageThreads.find((msg) => {
          const threadPk = `${tenantId}#${msg?.to === firmPhoneNumber ? msg?.from_ : msg?.to}`;
          return threadPk === messagePk;
        });

        // Keep in selection only if thread exists and is still unread
        return thread && thread.is_read === false;
      });

      // Update selection if any threads were removed
      if (validSelectedThreads.length !== selectedThreadsForMarkRead.length) {
        setSelectedThreadsForMarkRead(validSelectedThreads);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageThreads]);

  const handleMarkAsReadClick = async () => {
    // If no threads are selected, show warning
    if (selectedThreadsForMarkRead.length === 0) {
      addToast(false, toastConstant.toasterType.WARNING, 'Warning', 'Please select at least one thread to mark as read');
      return;
    }

    try {
      const firm_user_id = userContext?.userDetails?.firm_user_id;

      // Build payload - if select all is active, don't pass message_pk
      const payload = {
        user_id: firm_user_id,
      };

      // Only add message_pk if select all is NOT active
      if (!isSelectAllActive) {
        payload.message_pk = selectedThreadsForMarkRead;
      }

      // Call the API to mark messages as read
      const response = await markTextMessagesAsRead(payload);

      // Only proceed if API call was successful
      if (response) {
        // Clear selections first to hide the button immediately
        setSelectedThreadsForMarkRead([]);
        setIsSelectAllActive(false);

        // Refresh the text message thread list
        await fetchTextMessages(selectedContact?.contact_id, false, activeIndex === 0 ? '' : false, {
          practice_area_ids: selectedPracticeAreas,
        });

        // Refresh notification count
        if (loadNotificationCount) {
          loadNotificationCount(selectedPracticeAreas);
        }

        // Show success message
        addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Text messages marked as read successfully');
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
      addToast(false, toastConstant.toasterType.ERROR, 'Error', 'Failed to mark messages as read');
    }
  };

  // Only show "Mark as Read" button when threads are selected
  const hasSelectedThreads = selectedThreadsForMarkRead.length > 0;
  const showMarkAsRead = !isFromCase && !isFromLead && hasSelectedThreads;

  return (
    <div
      className="text-message-wrap text-message-tab-color px-2 pb-3"
      style={{
        height: window.innerWidth >= 992 ? `calc(100vh - ${navHeight}px - 25px)` : 'auto',
      }}
    >
      <GlobalLoader />
      <Header
        {...{
          isFromCase,
          isFromLead,
          showMarkAsRead,
          onMarkAsReadClick: handleMarkAsReadClick,
          hasSelectedThreads,
        }}
      />
      {userContext?.textMessageStatus === 'INACTIVE' ? (
        <SelectNumber />
      ) : (
        <div className="d-flex flex-lg-row flex-column h-100 overflow-hidden">
          <div className="d-flex flex-column col-lg-4 col-12 px-md-2 text-msg-list">
            <ThreadList
              {...{
                isFromCase,
                caseDetails,
                isFromLead,
                leadDetails,
                selectedContact,
                setSelectedContact,
                messageThreads,
                setMessageThreads,
                selectedThread,
                handleSelectThread,
                fetchTextMessages,
                activeIndex,
                setActiveIndex,
                loadThreads,
                isLoading,
                selectedPracticeAreas,
                setSelectedPracticeAreas,
                setPageCount,
                setIsLastPage,
                selectedThreadsForMarkRead,
                setSelectedThreadsForMarkRead,
                onMarkAsRead: handleMarkAsReadClick,
                isSelectAllActive,
                setIsSelectAllActive,
              }}
            />
          </div>
          <div className="d-flex flex-column col-lg-8 col-12 h-100 px-md-2 text-msg-view">
            {Boolean(selectedThread) ? (
              <MessageContainer
                {...{
                  setNewThreadActive,
                  handleSelectThread,
                  isFromCase,
                  caseDetails,
                  navigateToLatest,
                  selectedThread,
                  newThreadActive,
                  setMessageThreads,
                  onMountUnMount,
                  onOperationBreak,
                  fetchTextMessages,
                  selectedContact,
                  messageThreads,
                  isFromLead,
                }}
              />
            ) : (
              <div className="card-bg p-3 mt-3 h-100 ">
                <div className="d-flex flex-column align-items-center justify-content-center h-100 F-size16 start-conversation">
                  <i className="icon-chat" />
                  Select a Conversation
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
